import React, {useEffect, useMemo, useState} from 'react';
import {AnyCustomModelJson, resolveBaseModelName} from "../../api/json/CustomModelJson";
import {Box, Chip, Stack, Typography} from "@mui/material"
import {CustomModelRequests} from "../../api/requests/CustomModelRequests"
import {EndpointStatus, resolveEndpointStatusColor, resolveEndpointStatusLabel} from "../../api/enum/EndpointStatus"
import {
    DataGrid,
    GridCellParams,
    GridColDef,
    GridColumnVisibilityModel,
    GridRenderCellParams,
} from "@mui/x-data-grid"
import {useNavigate} from "react-router-dom"
import {useProjectData} from "../../hooks/useProjectData"
import {ModelPlatform, resolveNameForModelPlatform} from "../../api/enum/ModelPlatform"
import {TimeUtil} from "../../library/TimeUtil"
import InlineCode from "../common/InlineCode"
import ModelScoreCell from "../models/ModelScoreCell"
import CustomModelStatusInfo from "./CustomModelStatusInfo"
import CustomModelRowActionsMenu from "../models/CustomModelRowActionsMenu"

export type CustomModelTableColumn = "Created" | "Score" | "Platform" | "Model" | "Epochs" | "Training Rows" | "Template" | "Status"

interface FinetunesTableProps {
    customModels: AnyCustomModelJson[]
    columns: CustomModelTableColumn[]
    reload: () => void
}

// Map display-name keys in the existing column menu to DataGrid column `column` ids.
const COLUMN_FIELD_BY_LABEL: Record<CustomModelTableColumn, string> = {
    "Score": "score",
    "Platform": "platform",
    "Model": "baseModel",
    "Epochs": "numberOfEpochs",
    "Training Rows": "trainingRowCount",
    "Template": "template",
    "Created": "created",
    "Status": "status",
}

function CustomModelsTable({customModels, columns, reload}: FinetunesTableProps) {
    const {project} = useProjectData()
    const navigate = useNavigate()
    const [endpointStatuses, setEndpointStatuses] = useState<Record<string, EndpointStatus>>({})

    useEffect(() => {
        if (!project) return
        CustomModelRequests.projectEndpointStatuses(project.id)
            .then(setEndpointStatuses)
            .catch(() => {})
    }, [project, customModels])

    const gridColumns: GridColDef<AnyCustomModelJson>[] = useMemo(() => {
        if (!project) return []
        return [
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 180,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson>) => {
                    const endpointStatus = endpointStatuses[params.row.id]
                    const endpointLive = endpointStatus === EndpointStatus.STARTED || endpointStatus === EndpointStatus.STARTING
                    return (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{height: "100%"}}>
                            <Typography variant="body2">{params.row.name}</Typography>
                            {params.row.id === project.defaultCustomModelId &&
                                <Chip label="Default" size="small" color="primary"/>
                            }
                            {endpointLive &&
                                <Chip label={resolveEndpointStatusLabel(endpointStatus)}
                                      color={resolveEndpointStatusColor(endpointStatus)}
                                      size="small" variant="outlined"/>
                            }
                        </Stack>
                    )
                },
            },
            {
                field: 'score',
                headerName: 'Score',
                width: 110,
                sortable: true,
                filterable: false,
                disableColumnMenu: true,
                valueGetter: (_value, row: AnyCustomModelJson) =>
                    row.qualityCheck.stats?.score ?? null,
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson>) => (
                    <ModelScoreCell project={project} model={params.row}/>
                ),
            },
            {
                field: 'platform',
                headerName: 'Platform',
                width: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                valueGetter: (_value, row: AnyCustomModelJson) =>
                    resolveNameForModelPlatform(row.platform),
            },
            {
                field: 'baseModel',
                headerName: 'Base Model',
                flex: 1,
                minWidth: 180,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson>) => {
                    const customModel = params.row
                    const label = resolveBaseModelName(customModel)
                    return label ? <InlineCode>{label}</InlineCode> : <></>
                },
            },
            {
                field: 'numberOfEpochs',
                headerName: 'Epochs',
                width: 90,
                sortable: true,
                filterable: false,
                disableColumnMenu: true,
                align: 'right',
                headerAlign: 'right',
                valueGetter: (_value, row: AnyCustomModelJson) => row.numberOfEpochs,
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson, number>) => (
                    <Typography variant="body2">{params.value?.toLocaleString() ?? ""}</Typography>
                ),
            },
            {
                field: 'trainingRowCount',
                headerName: 'Training Rows',
                width: 150,
                sortable: true,
                filterable: false,
                disableColumnMenu: true,
                align: 'right',
                headerAlign: 'right',
                valueGetter: (_value, row: AnyCustomModelJson) =>
                    row.trainingRowCount ?? 0,
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson, number>) => (
                    <Typography variant="body2">{params.value?.toLocaleString() ?? ""}</Typography>
                ),
            },
            {
                field: 'template',
                headerName: 'Template',
                flex: 1,
                minWidth: 140,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson>) => (
                    <Typography variant="body2">{params.row.template.name}</Typography>
                ),
            },
            {
                field: 'created',
                headerName: 'Created',
                width: 130,
                sortable: true,
                filterable: false,
                disableColumnMenu: true,
                valueGetter: (_value, row: AnyCustomModelJson) =>
                    Date.parse(row.created),
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson>) => (
                    <Typography variant="body2" color="text.secondary">
                        {TimeUtil.ago(params.row.created)}
                    </Typography>
                ),
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson>) => (
                    <CustomModelStatusInfo customModel={params.row} sx={{height: "100%", justifyContent: "center"}}/>
                ),
            },
            {
                field: '__actions__',
                headerName: '',
                width: 56,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params: GridRenderCellParams<AnyCustomModelJson>) => (
                    <CustomModelRowActionsMenu model={params.row}
                                               didDelete={reload}
                                               didRename={reload}/>
                ),
            },
        ]
    }, [project, reload, endpointStatuses])

    const columnVisibilityModel: GridColumnVisibilityModel = useMemo(() => {
        const model: GridColumnVisibilityModel = {}
        ;(Object.keys(COLUMN_FIELD_BY_LABEL) as CustomModelTableColumn[]).forEach(label => {
            model[COLUMN_FIELD_BY_LABEL[label]] = columns.includes(label)
        })
        return model
    }, [columns])

    if (!project) return <></>

    return (
        <Box sx={{width: '100%'}}>
            <DataGrid
                autoHeight
                rows={customModels}
                columns={gridColumns}
                columnVisibilityModel={columnVisibilityModel}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                disableColumnMenu
                hideFooter
                initialState={{
                    sorting: {
                        sortModel: [{field: 'created', sort: 'desc'}],
                    },
                }}
                onCellClick={(params: GridCellParams) => {
                    if (params.field === '__actions__') return
                    if (params.field === 'template') {
                        navigate(`/projects/${project.id}/templates/${params.row.template.id}`)
                        return
                    }
                    navigate(`/projects/${project.id}/custom-models/${params.row.id}`)
                }}
                sx={{
                    backgroundColor: 'white',
                    '& .MuiDataGrid-row': {cursor: 'pointer'},
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                        outline: 'none',
                    },
                }}
            />
        </Box>
    )
}

export default CustomModelsTable
